import { Injectable, effect, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslationService } from '@core/services/translation.service';

const APP_SUFFIX = 'WAKI';

const SEGMENT_LABELS: Record<string, string> = {
  'dashboard': 'Dashboard',
  'businesses': 'Businesses',
  'business': 'Business',
  'general': '',
  'businesses-list': 'Business List',
  'businesses-registration': 'Business Registration',
  'branches': 'Branches',
  'list': 'List',
  'members': 'Members',
  'groups': 'Groups',
  'gates': 'Gates',
  'users': 'Users',
  'users-list': 'Users List',
  'finance': 'Finance',
  'sales': 'Sales',
  'inventory': 'Inventory',
  'marketing': 'Marketing',
  'events': 'Events',
  'notifications': 'Notifications',
  'advertisements': 'Advertisements',
  'attendance': 'Attendance',
  'dismissal-call': 'Dismissal Call',
  'dismissal': 'Dismissal',
  'cards': 'Cards',
  'individuals': 'Individuals',
  'individuals-list': 'Individuals List',
  'individuals-registration': 'Individual Registration',
  'dependents-list': 'Dependents',
  'profile': 'Profile',
  'add': 'Add',
  'edit': 'Edit',
  'create': 'Create',
  'registration': 'Registration',
  'registration-requests': 'Registration Requests',
  'auth': 'Sign In',
  'login': 'Sign In',
  'otp': 'Verify OTP',
  'marketplace': 'Marketplace',
  'subscriptions': 'Subscriptions',
  'admin': 'Admin',
  'waki-store': 'WAKI Store',
  'school': 'School',
  'transaction-logs': 'Transaction Logs',
  'categories': 'Categories',
  'brands': 'Brands',
  'products': 'Products',
  'membership': 'Membership',
  'catalogue-entry': 'Catalogue Entry',
};

const ENTITY_PROFILE_LABEL: Record<string, string> = {
  'businesses-list': 'Business Profile',
  'businesses': 'Business Profile',
  'branches': 'Branch Profile',
  'individuals-list': 'Individual Profile',
  'dependents-list': 'Dependent Profile',
  'users': 'User Profile',
  'users-list': 'User Profile',
  'businesses-registration': 'Registration Request',
  'individuals-registration': 'Registration Request',
  'subscriptions': 'Subscription',
  'products': 'Product',
  'categories': 'Category',
  'brands': 'Brand',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_RE = /^[0-9]+$/;

function isId(segment: string): boolean {
  return UUID_RE.test(segment) || NUMERIC_RE.test(segment);
}

function capitalize(word: string): string {
  if (!word) return '';
  return word.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function deriveTitleFromUrl(url: string): string {
  const segments = url.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (!segments.length) return 'Dashboard';

  const last = segments[segments.length - 1];
  const prev = segments[segments.length - 2];

  if (isId(last) && prev) {
    return ENTITY_PROFILE_LABEL[prev] ?? `${capitalize(prev)} Profile`;
  }

  if (SEGMENT_LABELS[last]) return SEGMENT_LABELS[last];
  return capitalize(last);
}

/** "Edit Request" and "edit-request" both become "edit-request" — same slug rule as page-header breadcrumbs. */
function slugify(label: string): string {
  return label.trim().toLowerCase().replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

@Injectable({ providedIn: 'root' })
export class WakiTitleStrategy extends TitleStrategy {
  private readonly i18n = inject(TranslationService);

  /** The English title of the current route; kept so a language switch can re-translate it. */
  private readonly pageTitle = signal<string | null>(null);

  constructor(private readonly title: Title) {
    super();
    // Re-applies the title when the language (or whitelabel vocabulary) changes
    // without a navigation; localize() reads both signals.
    effect(() => {
      const pageTitle = this.pageTitle();
      if (pageTitle !== null) this.title.setTitle(this.compose(pageTitle));
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const explicit = this.buildTitle(snapshot);
    const pageTitle = explicit ?? deriveTitleFromUrl(snapshot.url);
    this.pageTitle.set(pageTitle);
    this.title.setTitle(this.compose(pageTitle));
  }

  private compose(pageTitle: string): string {
    const localized = this.localize(pageTitle);
    return localized ? `${localized} · ${APP_SUFFIX}` : APP_SUFFIX;
  }

  /**
   * Route titles are English literals. In Arabic they are looked up by slug,
   * first under `pageTitle.*` and then `breadcrumb.*` (most titles name the same
   * page a breadcrumb does). English keeps the literal untouched, and a title
   * with no entry stays English rather than going blank.
   */
  private localize(pageTitle: string): string {
    if (!pageTitle || !this.i18n.isArabic()) return pageTitle;
    const slug = slugify(pageTitle);
    return this.i18n.tryT(`pageTitle.${slug}`) ?? this.i18n.tryT(`breadcrumb.${slug}`) ?? pageTitle;
  }
}
